import { createStore, unwrap } from "solid-js/store";
import { createSignal, createEffect, createMemo, Show, onMount, For, createResource } from "solid-js";
import { useParams, useNavigate } from "@solidjs/router";

import { getFile } from "~/utils";
import { Editable } from "~/components";
import Section from "~/components/Section";
import Sortable from "solid-sortablejs";

const STRUCTURE_VERSION = 2;

const emptyCollection = {
	unsaved: true,
	version: STRUCTURE_VERSION,
	name: "Name this Collection",
	sections: [
		{
			name: "Available Assets",
			meta: {},
			assets: [],
		},
	],
};
const upgrade = ( x ) => { }


const loadCollection = async ( key: string ): Promise<object> => {
	"use server";

	const collectionJson = await getFile( `genie-tools/collections/${key}` );

	// Ensure `Contents` is typed as an array and default to an empty array if it's undefined
	return collectionJson ? JSON.parse( collectionJson ) : {};
};

export default function Collection() {
	const params = useParams();

	const [collectionFetch, { refetch }] = createResource( async () => await loadCollection( params.id ) );
	const [collection, setCollection] = createStore( {
		"name": "Loading",
		"version": 1,
		"template": "marketing-steps-lc1",
		"sections": [
			{
				"name": "Loading",
				"assets": []
			}]
	} );

	createEffect( () => {
		console.log( collectionFetch.latest );

		if ( !collectionFetch.loading ) {

			setCollection( "name", collectionFetch.latest?.name );
			setCollection( "version", collectionFetch.latest?.version );
			setCollection( "template", collectionFetch.latest?.template );
			setCollection( "sections", collectionFetch.latest?.sections );

			collectionFetch.latest?.sections?.length > 0 &&  setCurrentSection( collectionFetch.latest?.sections[0] );
		}
	} )

	const [saving, setSaving] = createSignal( false );
	const [currentSection, setCurrentSection] = createSignal( 0 );

	const [tab, setTab] = createSignal( 'assets' );



	return (
		<div class="bg-white rounded-2xl shadow text-black" >
			<header class="flex items-center justify-between shadow px-6 py-4">
				<div class="flex items-center">
					<h1 class="text-xl font-semibold">{collection?.name} <span class="text-gray-500 text-sm">v{collection?.version ?? '1'}</span></h1>
				</div>
				<div class="space-x-4">
					<button class="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded">Save</button>
					<button class={`inline-flex flex-nowrap items-center bg-white border border-gray-300 hover:bg-gray-50 text-gray-800 font-medium py-2 px-4 rounded`} onClick={() => refetch()}>
						<Show when={collectionFetch.loading}>
							<span>
								<svg width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M10.14,1.16a11,11,0,0,0-9,8.92A1.59,1.59,0,0,0,2.46,12,1.52,1.52,0,0,0,4.11,10.7a8,8,0,0,1,6.66-6.61A1.42,1.42,0,0,0,12,2.69h0A1.57,1.57,0,0,0,10.14,1.16Z"><animateTransform attributeName="transform" type="rotate" dur="0.75s" values="0 12 12;360 12 12" repeatCount="indefinite" /></path></svg>
							</span>
						</Show>
						Reset
					</button>
				</div>
			</header>

			<div class="flex flex-1 pt-20 overflow-hidden">

				<aside class="w-1/4 bg-white border-r border-gray-200 overflow-auto">
					<ul class="space-y-1 p-4">
						<Sortable items={collection?.sections ?? []} setItems={e => {
							setCollection( "sections", e );
						}} idField="name">
							{section =>
								<li class="group flex items-center justify-between px-4 py-2 hover:bg-gray-100 cursor-pointer" onClick={() => {
									setCurrentSection( section );
								}}>
									<span class="flex items-center space-x-2">
										<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-layers h-5 w-5 text-gray-600"><polygon points="12 2 2 7 12 12 22 7 12 2"></polygon><polyline points="2 17 12 22 22 17"></polyline><polyline points="2 12 12 17 22 12"></polyline></svg>
										<span>{section.name?? "{Section}"}</span>
									</span>
									<span class="flex items-center space-x-2 opacity-0 group-hover:opacity-100">
										<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-edit h-4 w-4 text-gray-600 hover:text-gray-800"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
										<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-trash-2 h-4 w-4 text-red-500 hover:text-red-600"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>

										<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-menu h-4 w-4 text-grey-500 hover:text-grey-600"><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="18" x2="21" y2="18"></line></svg>
									</span>
								</li>
							}
						</Sortable>
					</ul>

					<button class="w-full py-2 bg-gray-100 hover:bg-gray-200 text-gray-700">+ Section</button>
				</aside>

				<main class="w-3/4 p-6 overflow-auto">
					<div class="border-b border-gray-200 mb-4">
						<nav class="flex space-x-4">
							<button id="tab-assets" class="pb-2 border-b-2 border-blue-600 font-semibold" onClick={() => setTab( 'assets' )}>Assets View</button>
							<button id="tab-json" class="pb-2 text-gray-500 hover:text-gray-700" onClick={() => setTab( 'json' )}>Raw JSON</button>
						</nav>
					</div>

					<section id="assets-view" classList={{ "space-y-6": true, hidden: tab() !== 'assets' }}>
						<div class="flex items-center justify-between">
							<h2 class="text-lg font-semibold">{currentSection()?.name?? "{Section}"}
								<button class="ml-2 p-1 hover:bg-gray-200 rounded"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-edit h-4 w-4 text-gray-600"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg></button>
							</h2>
						</div>
						<ul class="space-y-1 p-4 divide-y divide-gray-100">
							<Sortable items={currentSection()?.assets ?? []} setItems={e => {

								console.log( [...e] );
							}} idField="asset">
								{asset =>
									<li class="relative flex items-center justify-between px-4 py-2 hover:bg-gray-100 cursor-pointer gap-x-6" >
										<span class="flex items-center space-x-2">
											<span>{asset.asset}</span>
										</span>
										<span class="flex items-center space-x-2 opacity-0 group-hover:opacity-100">
											<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-edit h-4 w-4 text-gray-600 hover:text-gray-800"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
											<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-trash-2 h-4 w-4 text-red-500 hover:text-red-600"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>

											<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-menu h-4 w-4 text-grey-500 hover:text-grey-600"><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="18" x2="21" y2="18"></line></svg>
										</span>
									</li>
								}
							</Sortable>
						</ul>

						{/*
						<div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
							<div class="bg-white rounded-lg shadow p-4 flex items-center justify-between">
								<span class="flex items-center space-x-2">
									<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-file-text h-5 w-5 text-gray-500"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
									<span>landing-pages/market-insider-instant-download-v1</span>
								</span>
								<span class="flex items-center space-x-2 opacity-0 hover:opacity-100">
									<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-check h-4 w-4 hover:text-green-600"><polyline points="20 6 9 17 4 12"></polyline></svg>
									<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-x h-4 w-4 hover:text-red-600"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
									<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-move h-4 w-4 text-gray-400 cursor-move"><polyline points="5 9 2 12 5 15"></polyline><polyline points="9 5 12 2 15 5"></polyline><polyline points="15 19 12 22 9 19"></polyline><polyline points="19 9 22 12 19 15"></polyline><line x1="2" y1="12" x2="22" y2="12"></line><line x1="12" y1="2" x2="12" y2="22"></line></svg>
								</span>
							</div>
						</div>
						 */}
						<button class="mt-4 px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded">+ Add Asset</button>
					</section>

					<section id="raw-json" classList={{ "space-y-6": true, hidden: tab() !== 'json' }}>
						<div class="flex items-center justify-between mb-2">
							<h2 class="text-lg font-semibold">Raw JSON</h2>
							<button class="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded">Enable Editing</button>
						</div>
						<pre class="bg-gray-800 p-4 rounded  text-white  overflow-auto" style="height: 400px;">
							{JSON.stringify( collection, null, 2 )}
						</pre>
						<p>Done</p>
					</section>
				</main>
			</div>
		</div>
	);

}

/*

			<div class="container px-4 sm:px-6 lg:px-8 bg-white rounded-lg text-black">
				<div class="collection-box collection-header">
					<div class="input-grid">
						<span>Collection:</span>

						<input
							class="focussed"
							placeholder="Collections must have name"
							type="text"
							value={collection().name}
						/>
					</div>
				</div>

				<For each={collection().sections}>
					{( section ) => (
						<p>{JSON.stringify( section )}</p>
					)}
				</For>
			</div>


const upgrade = storeIndex => {
	const json = unwrap(app.store.allCollections[storeIndex]);

	switch (json.version) {
		case STRUCTURE_VERSION:
			break;

		case 1:
			json.sections = [];

			for (const [name, props] of Object.entries(json.folders)) {
				json.sections.push({
					...props,
					name,
				});
			}

			json.version = STRUCTURE_VERSION;

			break;

		default:
			json.sections = [];
			if (json.folders) {
				for (const [name, assets] of Object.entries(json.folders)) {
					json.sections.push({
						meta: { caption: "", description: "" },
						assets: assets ?? [],
						name,
					});
				}
			}
			break;
	}
	delete json.folders;
	json.version = STRUCTURE_VERSION;

	//p app.setStore("allCollections", storeIndex, json);

	return json;
};*/

export function XCollection() {
	const params = useParams<{ id: string }>();

	console.log( params );
	//p if (params.id !== "new") upgrade(params.id);

	const [filename, setFilename] = createSignal(
		//params.id.replace(".json", "")
	);

	const [collection, setCollection] = createSignal(
		params.id !== "new"
			? {} //p app.store.allCollections[params.id]
			: emptyCollection
	);

	const updateCollection = ( prop, value ) => {
		setCollection( c => ( { ...c, [prop]: value } ) );
	};

	//p createEffect(() => setCollection(app.store.allCollections[params.id]));

	const localSave = async message => {
		const data = {} //p unwrap(collection());
		delete data?.unsaved;
		delete data?.folders;

		//p await saveCollection({ template: filename(), collection: data });

		//p vNotify.success({ text: message });

		//p app.setStore("allCollections", params.id, data);
	};

	return (
		<div class="container px-4 sm:px-6 lg:px-8 bg-white rounded-lg">
			<div class="collection-box collection-header">
				<div class="input-grid">
					<span>Collection:</span>

					<input
						class="focussed"
						placeholder="Collections must have name"
						type="text"
						onChange={e => updateCollection( "name", e.target.value )}
						value={collection().name}
					/>
					<span>Collection Template:</span>
					<select
						aria-label="Collection Template"
						class="focussed"
						onChange={e => updateCollection( "template", e.target.value )}
						default="default"
						value={collection().template}>
						{/*p
						<For each={Object.keys(app.store.allAssets)}>
							{a => (
								<Show when={a.startsWith("collection")}>
									<option value={a}>{app.store.allAssets[a].name}</option>
								</Show>
							)}
						</For>
						 */}
					</select>

					<span>Filename:</span>
					<input
						class="focussed"
						placeholder={
							collection?.name.replaceAll( " ", "-" ).toLowerCase() ?? ""
						}
						type="text"
						onChange={e => setFilename( e.target.value )}
						value={filename()}
					/>
				</div>

				<div class="flex-row" style="margin-top: 2rem;">
					<button
						type="button"
						onClick={async () => await localSave( "Collection saved!" )}>
						Save
					</button>
					<button
						type="button"
						onClick={async () => {
							setFilename( f => `${f}-duplicate` );
							updateCollection( "name", `${collection?.name}-duplicate` );

							await localSave( "Collection duplicated!" );
						}}>
						Duplicate
					</button>
				</div>
			</div>

			<div id="sections">
				<h2>Sections</h2>
				<For each={collection?.sections}>
					{( section, index ) => (
						<>
							<Section
								name={section.name}
								section={section}
								updateName={newName => {
									if ( newName !== section.name ) {
										let sections = unwrap( collection?.sections );

										sections[index()].name = newName;

										setCollection( c => ( { ...c, sections } ) );
									}
								}}
								removeSection={() => {
									let sections = unwrap( collection?.sections ).filter(
										( s, i ) => i != index()
									);

									setCollection( c => ( { ...c, sections } ) );
								}}
								update={updatedSection => {
									let sections = unwrap( collection?.sections );

									sections[index()] = updatedSection;

									setCollection( c => ( { ...c, sections } ) );
								}}
							/>
						</>
					)}
				</For>
				<button
					type="button"
					onClick={() =>
						setCollection( c => ( {
							...c,
							sections: [...c.sections, { name: "New Section" }],
						} ) )
					}>
					Add Section
				</button>
			</div>
		</div >
	);
};

